import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { FormControlLabel, Switch, styled } from '@mui/material';
import useSubscriptionApi from '../api/useSubscriptionApi';
import SubscriptionPlanForm from '../components/subscriptionPlans/SubscriptionPlanForm';
import CommonLoader from '../components/loaders/CommonLoader';
import { IoCheckmarkCircle } from "react-icons/io5";
import { useAuth } from '../contexts/AuthContext';

const IOSSwitch = styled((props) => (
    <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
    width: 42,
    height: 26,
    padding: 0,
    '& .MuiSwitch-switchBase': {
        padding: 0,
        margin: 2,
        transitionDuration: '300ms',
        '&.Mui-checked': {
            transform: 'translateX(16px)',
            color: '#fff',
            '& + .MuiSwitch-track': {
                backgroundColor: '#65C466',
                opacity: 1,
                border: 0,
            },
        },
    },
    '& .MuiSwitch-thumb': {
        boxSizing: 'border-box',
        width: 22,
        height: 22,
    },
    '& .MuiSwitch-track': {
        borderRadius: 26 / 2,
        backgroundColor: '#E9E9EA',
        opacity: 1,
        transition: theme.transitions.create(['background-color'], {
            duration: 500,
        }),
    },
}));

const SubscriptionPlansPage = () => {
    const { getSubscriptionPlans, loading, addSubscriptionPlan, updateSubscriptionPlan } = useSubscriptionApi();
    const [openDialog, setOpenDialog] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [editData, setEditData] = useState(null);
    const [plans, setPlans] = useState([]);
    const { isActionAllowed } = useAuth();
    const handleAdd = () => { setEditData({ tags: [''] }); setOpenDialog(true) };

    const getPlans = async () => {
        const res = await getSubscriptionPlans();
        setPlans(res.data.payload.subscription_plan)
    }

    useEffect(() => {
        getPlans()
    }, [])

    const handleToggle = (id) => {
        let updatedPayload;

        setPlans((prev) => {
            const updated = prev.map((plan) =>
                plan._id === id ? { ...plan, is_live: !plan.is_live } : plan
            );
            updatedPayload = updated.find((plan) => plan._id === id);
            updateSubscriptionPlan(updatedPayload, false)
            return updated;
        });
    };

    const handleDelete = (id) => {
        const updated = plans.filter((plan) => plan._id !== id);
        setPlans(updated);
    };

    const handleEditClick = (plan) => {
        setEditData(plan);
        setOpenDialog(true);
    };

    const handleFormSubmit = async (formData) => {
        try {
            if (editData?._id) {
                await updateSubscriptionPlan(formData);
            } else {
                await addSubscriptionPlan(formData);
            }
            await getPlans();
            setOpenDialog(false);
        } catch (err) {
        } finally {
        }
    };

    return (
        <>
            <div>
                <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24'>
                    <h6 className='fw-semibold mb-0'>Subscription Plans</h6>
                    {isActionAllowed('SubscriptionPlans', 'create') ? (
                        <button className="btn btn-primary d-flex align-items-center gap-2" onClick={handleAdd}>
                            <Icon icon="ic:baseline-add" className="text-xl" />
                            Add Plan
                        </button>
                    ) : null}
                </div>
                <form className="navbar-search">
                    <input
                        type="text"
                        className="bg-base h-40-px w-auto"
                        name="search"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search"
                    />
                    <Icon icon="ion:search-outline" className="icon" />
                </form>

                {loading ? <CommonLoader /> :
                    <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 g-3 mt-3">
                        {plans
                            .filter((plan) =>
                                plan.title.toLowerCase().includes(searchQuery.toLowerCase())
                            )
                            .map((plan) => (
                                <div className="col" key={plan._id}>
                                    <div className="card h-100 shadow-sm rounded-4">
                                        <div className="card-body d-flex flex-column justify-content-between py-3">
                                            <div className="mb-3">
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <h6 className="fw-bold mb-0">{plan.title}</h6>
                                                    <div className="d-flex justify-content-between align-items-center ">
                                                        {isActionAllowed('SubscriptionPlans', 'edit') ? (
                                                            <FormControlLabel
                                                                control={
                                                                    <IOSSwitch
                                                                        checked={plan.is_live}
                                                                        onChange={() => handleToggle(plan._id)}
                                                                    />
                                                                }
                                                            />
                                                        ) : null}
                                                        {isActionAllowed('SubscriptionPlans', 'edit') ? (
                                                            <button
                                                                onClick={() => handleEditClick(plan)}
                                                                type="button"
                                                                className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                            >
                                                                <Icon icon="lucide:edit" className="menu-icon" />
                                                            </button>
                                                        ) : null}
                                                    </div>
                                                </div>
                                                <div className="mb-2">
                                                    <strong>Duration:</strong> {plan.duration} ({plan.durationInDays} days)
                                                </div>
                                                <div className="mb-2">
                                                    <strong>Index:</strong> {plan.index}
                                                </div>
                                            </div>

                                            <ul className="ai-chat-list card card-body border  mb-3">
                                                {/* <li className="list-group-item d-flex justify-content-between">
                                                    <span>Main Price</span>
                                                    <span>₹{plan.main_price}</span>
                                                </li> */}
                                                {/* <li className="list-group-item d-flex justify-content-between">
                                                    <span>GST</span>
                                                    <span>{plan.gst_persentage} %</span>
                                                </li> */}
                                                <li className="list-group-item d-flex justify-content-between fw-bold text-success">
                                                    <span>Selling Price</span>
                                                    <span>₹{plan.selling_price}</span>
                                                </li>
                                            </ul>

                                            <ul className="ai-chat-list card card-body border  mb-3">
                                                {plan.tags.length > 0 && plan.tags.map((tag) => {
                                                    return <li className="list-group-item d-flex justify-content-start align-items-start gap-2">
                                                        <span style={{ marginTop: "-5px", color: "green" }}><IoCheckmarkCircle size={20} /></span>
                                                        <span>{tag}</span>
                                                    </li>
                                                })}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            ))}
                    </div>
                }
            </div>
            <SubscriptionPlanForm
                openDialog={openDialog}
                handleClose={() => { setEditData({ tags: [''] }); setOpenDialog(false) }}
                handleSubmit={handleFormSubmit}
                loading={loading}
                initialData={editData}
            />
        </>
    );
};

export default SubscriptionPlansPage;
